import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { Toaster } from "sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import HealthMonitor from "@/components/HealthMonitor";
import { api } from "@/lib/api";
import { captureAttribution } from "@/lib/utm";
// Landing + AuthCallback stay in the main bundle — they're on the critical
// path for every cold visit (Landing) and every Google-OAuth return (Callback).
// Everything else is route-level code-split so the initial JS payload drops
// from ~528 KB → ~150 KB for an unauthenticated landing-page visit.
import Landing from "@/pages/Landing";
import AuthCallback from "@/pages/AuthCallback";
import "@/App.css";

// Route-level code splits — each chunk loads only when the user hits the route.
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const NewSession = lazy(() => import("@/pages/NewSession"));
const SessionDetail = lazy(() => import("@/pages/SessionDetail"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const Billing = lazy(() => import("@/pages/Billing"));
const Settings = lazy(() => import("@/pages/Settings"));
const PublicSession = lazy(() => import("@/pages/PublicSession"));
const HallOfVerdicts = lazy(() => import("@/pages/HallOfVerdicts"));
const Blog = lazy(() => import("@/pages/Blog"));
const Teams = lazy(() => import("@/pages/Teams"));
const TeamDetail = lazy(() => import("@/pages/TeamDetail"));
const TeamSetup = lazy(() => import("@/pages/TeamSetup"));
const InviteAccept = lazy(() => import("@/pages/InviteAccept"));
const Status = lazy(() => import("@/pages/Status"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const About = lazy(() => import("@/pages/About"));
const Launch = lazy(() => import("@/pages/Launch"));
const ValidateStartupIdea = lazy(() => import("@/pages/ValidateStartupIdea"));
const StartupIdeaTesting = lazy(() => import("@/pages/StartupIdeaTesting"));
const DigitalSharkTank = lazy(() => import("@/pages/DigitalSharkTank"));

const WhyAdvisorsLie = lazy(() =>
  import("@/pages/BlogPosts").then((m) => ({ default: m.WhyAdvisorsLie }))
);
const FiveIdeasThatFailed = lazy(() =>
  import("@/pages/BlogPosts").then((m) => ({ default: m.FiveIdeasThatFailed }))
);
const PromoKit = lazy(() => import("@/pages/PromoKit"));
const PreviewStats = lazy(() => import("@/pages/PreviewStats"));
const AdminTestimonials = lazy(() => import("@/pages/AdminTestimonials"));
const Receipts = lazy(() => import("@/pages/Receipts"));
const BrandingKit = lazy(() => import("@/pages/BrandingKit"));
const BrandGuide = lazy(() => import("@/pages/BrandGuide"));
const Feedback = lazy(() => import("@/pages/Feedback"));
const Studio = lazy(() => import("@/pages/Studio"));
const Panel = lazy(() => import("@/pages/Panel"));
// BlogPosts.jsx has two named exports — wrap each into the `default` shape
// React.lazy requires.
const WhyValidationFlatter = lazy(() =>
  import("@/pages/BlogPosts").then((m) => ({ default: m.WhyValidationFlatter }))
);
const KillCriteriaFirst = lazy(() =>
  import("@/pages/BlogPosts").then((m) => ({ default: m.KillCriteriaFirst }))
);
const HowToValidateStartupIdea = lazy(() =>
  import("@/pages/BlogPosts").then((m) => ({ default: m.HowToValidateStartupIdea }))
);
const PivotOrPersevereFramework = lazy(() =>
  import("@/pages/BlogPosts").then((m) => ({ default: m.PivotOrPersevereFramework }))
);
const WhatIsAPivotScore = lazy(() =>
  import("@/pages/BlogPosts").then((m) => ({ default: m.WhatIsAPivotScore }))
);
const FiveVCQuestions = lazy(() =>
  import("@/pages/BlogPosts").then((m) => ({ default: m.FiveVCQuestions }))
);
const AIvsCustomerDiscovery = lazy(() =>
  import("@/pages/BlogPosts").then((m) => ({ default: m.AIvsCustomerDiscovery }))
);
const SixtySecondPreMortem = lazy(() =>
  import("@/pages/BlogPosts").then((m) => ({ default: m.SixtySecondPreMortem }))
);
const FAQ = lazy(() => import("@/pages/FAQ"));
const Ideas = lazy(() => import("@/pages/Ideas"));
const IdeaVerdict = lazy(() => import("@/pages/IdeaVerdict"));
const Compare = lazy(() => import("@/pages/Compare"));
const LaunchKit = lazy(() => import("@/pages/LaunchKit"));
const Affiliates = lazy(() => import("@/pages/Affiliates"));
const PartnerLanding = lazy(() => import("@/pages/PartnerLanding"));
const AdminAffiliates = lazy(() => import("@/pages/AdminAffiliates"));

// Cheap brutalist fallback while a route chunk downloads. Doesn't flash a
// loader for fast-cached navigations — the chunks resolve sub-100 ms on
// repeat visits.
function RouteFallback() {
  return (
    <div className="min-h-[40vh] flex items-center justify-center" data-testid="route-loading">
      <div className="font-mono text-xs uppercase tracking-[0.3em] text-white/35">
        Loading<span className="cursor-blink" />
      </div>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();

  useEffect(() => {
    captureAttribution();
  }, []);

  // Google Analytics 4 — SPA pageview tracker. gtag auto-tracks the
  // first hard-navigation, but react-router pushState updates need a
  // manual `gtag('event', 'page_view', ...)` on every path change or
  // the funnel will only show `/` and never `/pricing`, `/panel`, etc.
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    window.gtag("event", "page_view", {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location.pathname, location.search]);

  // Referral capture: any visit with `?r=<code>` triggers a one-time
  // POST to /referrals/track so a later paid checkout can credit the
  // referrer. Runs client-side (backend needs a request context) and
  // is deduped per code per tab via sessionStorage. No auth header —
  // anon visitors need to be attributable too. Fails silently.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get("r");
    if (!code) return;
    const key = `pp_ref_tracked_${code}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
    } catch {
      /* private mode — skip dedupe, still try to track */
    }
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/referrals/track`;
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code }),
    }).catch(() => {
      /* silent — decorative */
    });
  }, [location.search]);
  // Synchronous fragment check — handle Emergent OAuth callback before any other route runs
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <Nav />
      <main className="grain min-h-[calc(100vh-4rem)] relative">
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/share/:share_id" element={<PublicSession />} />
            <Route path="/p/:share_id" element={<PublicSession />} />
            <Route path="/ideas" element={<Ideas />} />
            <Route path="/ideas/:slug" element={<IdeaVerdict />} />
            <Route path="/vs" element={<Compare />} />
            <Route path="/vs/:slug" element={<Compare />} />
            <Route path="/affiliates" element={<Affiliates />} />
            <Route path="/with/:code" element={<PartnerLanding />} />
            <Route
              path="/admin/affiliates"
              element={
                <ProtectedRoute>
                  <AdminAffiliates />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/launch-kit"
              element={
                <ProtectedRoute>
                  <LaunchKit />
                </ProtectedRoute>
              }
            />
            <Route path="/hall" element={<HallOfVerdicts />} />
            <Route path="/blog" element={<Blog />} />
            <Route
              path="/blog/why-validation-tools-flatter-founders"
              element={<WhyValidationFlatter />}
            />
            <Route
              path="/blog/kill-criteria-before-you-start"
              element={<KillCriteriaFirst />}
            />
            <Route
              path="/blog/how-to-validate-a-startup-idea"
              element={<HowToValidateStartupIdea />}
            />
            <Route
              path="/blog/pivot-or-persevere-framework"
              element={<PivotOrPersevereFramework />}
            />
            <Route
              path="/blog/what-is-a-pivot-score"
              element={<WhatIsAPivotScore />}
            />
            <Route
              path="/blog/5-vc-questions-to-pre-answer"
              element={<FiveVCQuestions />}
            />
            <Route
              path="/blog/ai-vs-customer-discovery"
              element={<AIvsCustomerDiscovery />}
            />
            <Route
              path="/blog/60-second-startup-pre-mortem"
              element={<SixtySecondPreMortem />}
            />
            <Route
              path="/blog/why-advisors-lie-about-startup-ideas"
              element={<WhyAdvisorsLie />}
            />
            <Route
              path="/blog/startup-ideas-that-failed-validation"
              element={<FiveIdeasThatFailed />}
            />
            <Route path="/validate-startup-idea" element={<ValidateStartupIdea />} />
            <Route path="/startup-idea-testing" element={<StartupIdeaTesting />} />
            <Route path="/digital-shark-tank" element={<DigitalSharkTank />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/feedback" element={<Feedback />} />
            <Route path="/panel" element={<Panel />} />
            <Route path="/receipts" element={<Receipts />} />
            <Route path="/brand-guide" element={<BrandGuide />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/new"
              element={
                <ProtectedRoute>
                  <NewSession />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sessions/:id"
              element={
                <ProtectedRoute>
                  <SessionDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/billing"
              element={
                <ProtectedRoute>
                  <Billing />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teams"
              element={
                <ProtectedRoute>
                  <Teams />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teams/setup/:stripe_session_id"
              element={
                <ProtectedRoute>
                  <TeamSetup />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teams/:team_id"
              element={
                <ProtectedRoute>
                  <TeamDetail />
                </ProtectedRoute>
              }
            />
            <Route path="/invite/:invite_token" element={<InviteAccept />} />
            <Route path="/status" element={<Status />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/about" element={<About />} />
            <Route path="/launch" element={<Launch />} />
            {/* Aliases for social bios — both /producthunt and /ph resolve
                to the same launch anchor so we can promote either shorter
                URL without a redirect chain. Uses HTTP 301 semantic via
                react-router's Navigate replace. */}
            <Route path="/producthunt" element={<Navigate to="/launch" replace />} />
            <Route path="/ph" element={<Navigate to="/launch" replace />} />
            <Route
              path="/promo-kit"
              element={
                <ProtectedRoute>
                  <PromoKit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/preview-stats"
              element={
                <ProtectedRoute>
                  <PreviewStats />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/testimonials"
              element={
                <ProtectedRoute>
                  <AdminTestimonials />
                </ProtectedRoute>
              }
            />
            <Route
              path="/branding-kit"
              element={
                <ProtectedRoute>
                  <BrandingKit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/studio"
              element={
                <ProtectedRoute>
                  <Studio />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Landing />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <HealthMonitor />
            <AppRouter />
            <Toaster
              theme="dark"
              position="top-right"
              toastOptions={{
                style: {
                  background: "#0a0a0a",
                  color: "#F5F5F5",
                  border: "1px solid rgba(255,255,255,0.15)",
                  borderRadius: 0,
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: "13px",
                },
              }}
            />
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}
